import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Match, 
  Team, 
  getMatch, 
  getTeam 
} from '../services/matchService';

const MatchDetailsPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();

  const [match, setMatch] = useState<Match | null>(null);
  const [teamA, setTeamA] = useState<Team | null>(null);
  const [teamB, setTeamB] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatchDetails = async () => {
      try {
        setLoading(true);

        if (!matchId) {
          navigate('/create-match');
          return;
        }

        const fetchedMatch = await getMatch(matchId);
        
        if (!fetchedMatch) {
          navigate('/create-match');
          return;
        }

        setMatch(fetchedMatch);

        // Charger les détails des équipes avec gestion des IDs manquants
        const loadTeam = async (teamData?: { id?: string; name: string }): Promise<Team | null> => {
          if (!teamData || !teamData.id) {
            // Créer une équipe par défaut si l'ID est manquant
            return {
              id: '',
              name: teamData?.name || 'Équipe sans nom',
              players: []
            };
          }

          try {
            const fetchedTeam = await getTeam(teamData.id);
            return fetchedTeam || {
              id: teamData.id,
              name: teamData.name,
              players: []
            };
          } catch (error) {
            console.error('Erreur lors du chargement de l\'équipe:', error);
            return {
              id: teamData.id,
              name: teamData.name,
              players: []
            };
          }
        };

        // Charger les équipes A et B
        const loadedTeamA = await loadTeam(fetchedMatch.teamA);
        const loadedTeamB = await loadTeam(fetchedMatch.teamB);

        setTeamA(loadedTeamA);
        setTeamB(loadedTeamB);
      } catch (error) {
        console.error('Erreur lors du chargement des détails du match:', error);
        navigate('/create-match');
      } finally {
        setLoading(false);
      }
    };

    fetchMatchDetails();
  }, [matchId, navigate]);

  if (loading) {
    return <div className="container mx-auto p-6 text-center">Chargement...</div>;
  }

  if (!match || !teamA) {
    return (
      <div className="container mx-auto p-6 text-center">
        <p>Aucun match trouvé</p>
        <button 
          onClick={() => navigate('/create-match')}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
        >
          Créer un Nouveau Match
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Détails du Match</h1>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-4">{teamA.name}</h2>
          <p className="text-4xl font-bold">{match.scoreA}</p>
          
          <div className="mt-4">
            <h3 className="text-lg font-semibold">Joueurs</h3>
            <div className="grid grid-cols-3 gap-2">
              {teamA.players.map((player, index) => (
                <div 
                  key={index} 
                  className="p-2 bg-gray-100 rounded text-center"
                >
                  {player.number} - {player.firstName}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-4">{teamB?.name || 'Équipe B'}</h2>
          <p className="text-4xl font-bold">{match.scoreB}</p>
          
          <div className="mt-4">
            <h3 className="text-lg font-semibold">Joueurs</h3>
            <div className="grid grid-cols-3 gap-2">
              {teamB?.players?.map((player, index) => (
                <div 
                  key={index} 
                  className="p-2 bg-gray-100 rounded text-center"
                >
                  {player.number} - {player.firstName}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white shadow-md rounded-lg p-6">
        <h3 className="text-xl font-bold mb-4">Événements du Match</h3>
        {match.events && match.events.length > 0 ? (
          <ul className="space-y-2">
            {match.events.map((event, index) => (
              <li 
                key={index} 
                className="p-2 bg-gray-100 rounded flex justify-between"
              >
                <span>{event.type}</span>
                <span>{event.player}</span>
                <span>{event.minute}'</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">Aucun événement pour le moment</p>
        )}
      </div>
    </div>
  );
};

export default MatchDetailsPage;
